import { zodResolver } from "@hookform/resolvers/zod";
import {
  Image as IImage,
  clientQueryKeys,
  useCreateClientDataConsent,
} from "@verde/entities";
import { DocumentIcon } from "@verde/icons";
import { Image } from "@verde/modules";
import { Alert, Button, Link } from "@verde/ui-core";
import { queryClient, z } from "@verde/utils";
import React, { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useAuthorizationDialog } from "./authorization-dialog";

const schema = z.object({
  SCR: IImage,
});
type Schema = z.infer<typeof schema>;

function AuthorizationDialogAttachment() {
  const { client, onClose } = useAuthorizationDialog();
  const create = useCreateClientDataConsent(client?.id);

  const {
    watch,
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      SCR: undefined,
    },
  });

  const scrValue = watch("SCR");

  const hasDocumentError = useMemo(() => {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const error = create.error as any;
    const errors = error?.response?.data?.["documents.scr"] as Array<string>;
    return !!scrValue && !!errors?.length;
  }, [scrValue, create.error]);

  const handleOnSubmit = useCallback(
    async ({ SCR }: Schema) => {
      await create.mutateAsync({
        kind: "scr",
        channel: "attachment",
        has_data_consent: true,
        documents_attributes: [
          {
            kind: "SCR",
            image_id: SCR.id,
          },
        ],
      });

      await queryClient.invalidateQueries(
        clientQueryKeys.detail.data_consent(client?.id),
      );

      onClose?.();
    },
    [create, client, onClose],
  );

  return (
    <form className="grid gap-4" onSubmit={handleSubmit(handleOnSubmit)}>
      <Alert>
        O documento anexado deve ser uma autorização simples, com a assinatura
        (rubrica pessoal) do produtor, seguido do número de seu CPF.
      </Alert>

      <div className="grid gap-4 rounded border border-neutral-light-2 p-4">
        <Image.Field
          control={control}
          label="Autorização de consulta CPF"
          name="SCR"
          kind={hasDocumentError ? "alert" : "default"}
        />

        {hasDocumentError && (
          <Alert kind="error">
            O arquivo enviado não parece estar assinado. Por favor, verifique e
            envie o documento assinado pelo produtor.
          </Alert>
        )}

        {!hasDocumentError && (
          <Alert>
            <p className="text-sm text-neutral-dark-2">
              Documento modelo: Faça{" "}
              <Link
                download
                className="text-sm text-neutral-dark-2 underline"
                href="https://docs.google.com/document/d/1ZWNyLUUUbBwEqJvypA3zOdkgyRD76NbcA4NQUgJ09eU/export?format=pdf"
              >
                download aqui
              </Link>{" "}
              do documento modelo para autorização.
            </p>
          </Alert>
        )}
      </div>

      <hr />

      <Button
        type="submit"
        size="lg"
        icon={<DocumentIcon />}
        loading={create.isLoading}
        disabled={isDirty === false}
      >
        Anexar documento
      </Button>
    </form>
  );
}

export default React.memo(AuthorizationDialogAttachment);
